import { useMemo, useState } from "react";
import {
  FAMILIES,
  formatRiel,
  formatUsd,
  isSellable,
  type Family,
  type Order,
  type Product,
} from "@moomoo/core";
import type { Translate } from "../lib/i18n.js";
import { Countdown, Stock, Counter, tileFor } from "./bits.js";
import type { AppState } from "../lib/store.js";

/** The bottles most people buy, shown first. Eleven products need no search box. */
const FIRST = ["FRESH-MILK-2L", "FRESH-MILK-1L", "KIDS-MILK-200ML", "BARISTA-MILK-2L", "A2-MILK-2L-PLASTIC"];

const FAMILY_LABEL: Record<Family | "all", { en: string; kh: string }> = {
  all: { en: "All", kh: "ទាំងអស់" },
  fresh: { en: "Fresh", kh: "ស្រស់" },
  a2: { en: "A2", kh: "A2" },
  barista: { en: "Barista", kh: "បារីស្តា" },
  light: { en: "Low fat", kh: "ខ្លាញ់ទាប" },
  kids: { en: "Kids", kh: "កុមារ" },
};

/**
 * One product, photograph first. The add control sits on the bottle rather
 * than in a row of chrome beneath it, which is what makes a shelf of these
 * read as product rather than as a table of records.
 */
function Tile({
  product,
  qty,
  lang,
  t,
  onBump,
}: {
  product: Product;
  qty: number;
  lang: "en" | "kh";
  t: Translate;
  onBump: (product: Product, direction: 1 | -1) => void;
}) {
  const gone = !isSellable(product);
  return (
    <div className={gone ? "tile gone" : "tile"}>
      <div className="shot">
        <img src={tileFor(product.code)} alt={product.nameEn} loading="lazy" width={300} height={300} />
        {gone ? (
          <button type="button" className="add" disabled aria-label={t("soldOut")}>
            +
          </button>
        ) : qty > 0 ? (
          <Counter
            qty={qty}
            label={product.nameEn}
            onDown={() => onBump(product, -1)}
            onUp={() => onBump(product, 1)}
          />
        ) : (
          <button
            type="button"
            className="add"
            onClick={() => onBump(product, 1)}
            aria-label={t("addLabel", product.nameEn)}
          >
            +
          </button>
        )}
      </div>
      <div className="nm">{lang === "kh" ? product.nameKh : product.nameEn}</div>
      <div className="alt">{lang === "kh" ? product.nameEn : product.nameKh}</div>
      <div className="price">
        <span className="usd">{formatUsd(product.price)}</span>
        <span className="riel">{formatRiel(product.price)}</span>
      </div>
      <Stock product={product} t={t} />
    </div>
  );
}

export function Shop({
  state,
  t,
  lang,
  onBump,
  onOpenCart,
  onReorder,
  foot,
}: {
  state: AppState;
  t: Translate;
  lang: "en" | "kh";
  onBump: (product: Product, direction: 1 | -1) => void;
  onOpenCart: () => void;
  onReorder: (order: Order) => void;
  /** Quiet things that belong at the end of the shelf, not in the customer's face. */
  foot?: React.ReactNode;
}) {
  const [family, setFamily] = useState<Family | "all">("all");

  const visible = useMemo(() => {
    const rank = (p: Product) => {
      const i = FIRST.indexOf(p.code);
      return i === -1 ? FIRST.length : i;
    };
    return state.products
      .filter((p) => family === "all" || p.family === family)
      .slice()
      .sort((a, b) => rank(a) - rank(b));
  }, [state.products, family]);

  if (!state.ready) {
    return (
      <div className="pad">
        <div className="sec" style={{ padding: "0 0 12px" }}>
          <h2>{t("loading")}</h2>
        </div>
        <div className="grid" style={{ padding: 0 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton" />
          ))}
        </div>
      </div>
    );
  }

  // the store keeps "<qty>|<code>" so the sentence can be built in either language
  const clamp = (() => {
    if (!state.clampNotice) return undefined;
    const [qty, code] = state.clampNotice.split("|");
    const product = state.products.find((p) => p.code === code);
    const name = product ? (lang === "kh" ? product.nameKh : product.nameEn) : code;
    return t("clampNotice", qty ?? "", name ?? "");
  })();

  return (
    <>
      <Countdown slot={state.slot} t={t} />

      {state.stale ? <div className="banner watch">{t("staleWarning")}</div> : null}
      {state.order && state.totals.bottles === 0 && state.order.lines.length > 0 ? (
        <div className="pad left" style={{ paddingBottom: 0 }}>
          <button type="button" className="btn quiet" style={{ marginTop: 0 }} onClick={() => onReorder(state.order as Order)}>
            {t("reorderLast")}
          </button>
        </div>
      ) : null}
      {state.channel === "wholesale" ? <div className="banner watch">{t("crateStep")}</div> : null}
      {clamp ? <div className="banner watch">{clamp}</div> : null}

      <div className="chips" role="tablist" aria-label={t("allMilk")}>
        {(["all", ...FAMILIES] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-pressed={family === key}
            onClick={() => setFamily(key)}
          >
            {FAMILY_LABEL[key][lang]}
          </button>
        ))}
      </div>

      <div className="sec">
        <h2>{t("allMilk")}</h2>
        <span>
          {visible.length} {t("products")}
        </span>
      </div>

      {visible.length === 0 ? (
        <div className="empty">
          <b>{t("noMatch")}</b>
        </div>
      ) : (
        <div className="grid">
          {visible.map((p) => (
            <Tile
              key={p.code}
              product={p}
              qty={state.lines[p.code] ?? 0}
              lang={lang}
              t={t}
              onBump={onBump}
            />
          ))}
        </div>
      )}

      {foot ? <div className="shopfoot">{foot}</div> : null}

      {state.totals.bottles > 0 ? (
        <div className="totalbar">
          <div className="count">
            {state.totals.bottles} {t("bottles")}
            <b>{formatUsd(state.totals.total)}</b>
          </div>
          <button type="button" className="go" onClick={onOpenCart}>
            {t("viewCart")}
          </button>
        </div>
      ) : null}
    </>
  );
}
